import { useState, useEffect } from 'react'
import { 
  Plus, 
  MagnifyingGlass, 
  DotsThreeVertical,
  User,
  Envelope,
  Phone,
  Calendar,
  Eye,
  PencilSimple,
  Trash,
  Download,
  Funnel,
  Crown
} from 'phosphor-react'
import { Menu } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

// Hook para autenticação
const useAuth = () => {
  const token = localStorage.getItem('admin_token')
  return { token }
}

interface BonusAccount {
  id: string;
  name: string;
  email: string;
  plan: string;
  trialEndsAt: string;
  daysRemaining: number;
}

interface User {
  id: string;
  name: string;
  email: string;
}

const BonusAccounts: React.FC = () => {
  const { token } = useAuth()
  const [bonusAccounts, setBonusAccounts] = useState<BonusAccount[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [selectedDuration, setSelectedDuration] = useState('30d');
  const [reason, setReason] = useState('');
  const [editingAccount, setEditingAccount] = useState<BonusAccount | null>(null);

  useEffect(() => {
    if (token) {
      fetchBonusAccounts();
      fetchUsers();
    }
  }, [token]);

        const fetchBonusAccounts = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/subscription/bonus`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao buscar contas bonificadas')
      }
      
      const data = await response.json();
      setBonusAccounts(data.bonusAccounts);
    } catch (error) {
      console.error('Erro ao buscar contas bonificadas:', error);
      toast.error('Erro ao carregar contas bonificadas')
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
              const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao buscar usuários')
      }
      
      const data = await response.json();
      setUsers(data.users);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast.error('Erro ao carregar usuários')
    }
  };

  const createBonusAccount = async () => {
    if (!selectedUser) {
      toast.error('Selecione um usuário');
      return;
    }

    try {
              const response = await fetch(`${API_BASE_URL}/subscription/bonus`, {
          method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: selectedUser,
          plan: selectedPlan,
          duration: selectedDuration,
          reason
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao criar conta bonificada')
      }

      toast.success('Conta bonificada criada com sucesso!');
      setShowCreateModal(false);
      setSelectedUser('');
      setSelectedPlan('premium');
      setSelectedDuration('30d');
      setReason('');
      fetchBonusAccounts();
    } catch (error) {
      console.error('Erro ao criar conta bonificada:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar conta bonificada');
    }
  };

  const editBonusAccount = async () => {
    if (!editingAccount) return;

    try {
              const response = await fetch(`${API_BASE_URL}/subscription/bonus/${editingAccount.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: selectedPlan,
          duration: selectedDuration,
          reason
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao editar conta bonificada')
      }

      toast.success('Conta bonificada atualizada com sucesso!');
      setShowEditModal(false);
      setEditingAccount(null);
      setSelectedPlan('premium');
      setSelectedDuration('30d');
      setReason('');
      fetchBonusAccounts();
    } catch (error) {
      console.error('Erro ao editar conta bonificada:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao editar conta bonificada');
    }
  };

  const removeBonusAccount = async (accountId: string) => {
    if (!confirm('Tem certeza que deseja remover esta conta bonificada?')) {
      return;
    }

    try {
              const response = await fetch(`${API_BASE_URL}/subscription/bonus/${accountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao remover conta bonificada')
      }

      toast.success('Conta bonificada removida com sucesso!');
      fetchBonusAccounts();
    } catch (error) {
      console.error('Erro ao remover conta bonificada:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao remover conta bonificada');
    }
  };

  const handleEdit = (account: BonusAccount) => {
    setEditingAccount(account);
    setSelectedPlan(account.plan || 'premium');
    setSelectedDuration('30d'); // Reset para duração padrão
    setReason('');
    setShowEditModal(true);
  };

  const resetForm = () => {
    setSelectedUser('');
    setSelectedPlan('premium');
    setSelectedDuration('30d');
    setReason('');
    setEditingAccount(null);
  };

  const getStatusColor = (daysRemaining: number) => {
    if (daysRemaining <= 7) return 'text-red-600';
    if (daysRemaining <= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'premium':
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'annual':
        return <Crown className="w-5 h-5 text-purple-500" />;
      case 'professional':
        return <Crown className="w-5 h-5 text-blue-500" />;
      default:
        return <Crown className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contas Bonificadas</h1>
          <p className="text-gray-600">Gerencie contas com acesso gratuito ao sistema</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <User size={20} />
          Nova Conta Bonificada
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <User size={24} className="text-blue-600" />
            <h2 className="text-lg font-semibold">Contas Ativas ({bonusAccounts.length})</h2>
          </div>

          {bonusAccounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Nenhuma conta bonificada encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Usuário</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Plano</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Expira em</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Dias Restantes</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {bonusAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{account.name}</div>
                          <div className="text-sm text-gray-500">{account.email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getPlanIcon(account.plan)}
                          <span className="capitalize">{account.plan}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-sm">
                            {new Date(account.trialEndsAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${getStatusColor(account.daysRemaining)}`}>
                          {account.daysRemaining} dias
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(account)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Editar"
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => removeBonusAccount(account.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remover"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nova Conta Bonificada</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usuário
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione um usuário</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plano
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="premium">Premium</option>
                  <option value="annual">Anual</option>
                  <option value="professional">Profissional</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duração
                </label>
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="7d">7 dias</option>
                  <option value="30d">30 dias</option>
                  <option value="90d">90 dias</option>
                  <option value="1y">1 ano</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo (opcional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Motivo da bonificação..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={createBonusAccount}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && editingAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Editar Conta Bonificada</h3>
            <p className="text-sm text-gray-600 mb-4">
              {editingAccount.name} ({editingAccount.email})
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plano
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="premium">Premium</option>
                  <option value="annual">Anual</option>
                  <option value="professional">Profissional</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Duração
                </label>
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="7d">7 dias</option>
                  <option value="30d">30 dias</option>
                  <option value="90d">90 dias</option>
                  <option value="1y">1 ano</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo da Alteração (opcional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Motivo da alteração..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={editBonusAccount}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Atualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonusAccounts; 